import React, { useState } from "react";
import { ChevronDown, ChevronUp, Loader2, AlertTriangle, CheckCircle, Stethoscope } from "lucide-react";
import type { ChatMessage, SuspectedDocumentContradiction } from "@/types/chat";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ContradictionsProps {
  message: ChatMessage;
  scanningStatus?: "analyzing" | "completed" | "error" | null;
}



export function Contradictions({ message, scanningStatus }: ContradictionsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const contradictions = message.suspected_document_contradictions || [];
  
  // Show scanning status with spinner icon
  if (scanningStatus === "analyzing") {
    return (
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Code Doctor is reviewing sources automatically for potential conflicts...</span>
          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md">BETA</span>
          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
        </div>
      </div>
    );
  }
  
  // Show "no issues found" with checkmark icon
  if (scanningStatus === "completed" && contradictions.length === 0) {
    return (
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Code Doctor found no conflicts</span>
          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md">BETA</span>
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
        </div>
      </div>
    );
  }
  
  // Show error state with alert icon
  if (scanningStatus === "error") {
    return (
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Code Doctor review error</span>
          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md">BETA</span>
          <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
        </div>
      </div>
    );
  }
  
  // Show "no suspected conflicts" for messages without contradictions but not currently scanning
  if (contradictions.length === 0 && !scanningStatus) {
    return (
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Code Doctor found no conflicts</span>
          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md">BETA</span>
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
        </div>
      </div>
    );
  }
  
  // Only show expandable contradictions section if there are actual contradictions
  if (contradictions.length === 0) {
    return null;
  }

  return (
    <aside className="additional-info-dropdown mb-4">
      <div
        role="button"
        className="additional-info-dropdown-title mb-2"
        style={{ cursor: "pointer" }}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => (e.key === "Enter" ? setIsExpanded(!isExpanded) : null)}
        tabIndex={0}
      >
        <div className="flex flex-row items-center">
          <Stethoscope className="h-4 w-4 text-muted-foreground mr-2" />
          <p className="text-sm text-muted-foreground mr-2">
            Code Doctor found {contradictions.length} suspected conflict{contradictions.length !== 1 ? 's' : ''}
          </p>
          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md mr-2">BETA</span>
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mr-2" />
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transform transition-transform duration-200 ${
              isExpanded ? "rotate-180" : "rotate-0"
            }`}
          />
        </div>
      </div>
      
      {isExpanded && (
        <div className="p2 additional-info-content pt-4 mt-2 border-t border-gray-300">
          {contradictions.map((contradiction: SuspectedDocumentContradiction, index: number) => (
            <div key={contradiction.id} className="mb-4">
              <p className="text-sm text-foreground mb-2">
                <span className="font-medium">Suspected Conflict:</span> {contradiction.explanation}
              </p>
              
              {contradiction.document_sources && (
                <p className="text-xs text-muted-foreground">
                  <strong>Sources:</strong> {contradiction.document_sources}
                </p>
              )}
            </div>
          ))}
          
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              Automated document review conducted with AI. Verify results.
            </p>
          </div>
        </div>
      )}
    </aside>
  );
}

export default Contradictions;